import React, { useState, useEffect } from 'react';
import api from '../utils/axiosInstance';
import { useLocation, useNavigate } from 'react-router-dom';
import { useModal } from '../context/ModalContext';
import { FaCheckCircle, FaEye, FaEyeSlash } from 'react-icons/fa';
import Recaptcha from '../components/Recaptcha';

export default function Signup() {
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    acceptTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [postAuthNext, setPostAuthNext] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const modal = useModal();

  // Capture pre-signup tool config from walytic.com/free-tools (e.g.
  // ?widgetConfig=<base64>&next=/whatsapp-widget) and stash it across
  // the signup → OTP → login → destination flow via sessionStorage.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const widgetConfig = params.get('widgetConfig');
      const next = params.get('next');
      const invite = params.get('invite');
      if (widgetConfig) sessionStorage.setItem('walytic_widget_prefill', widgetConfig);
      if (next && next.startsWith('/') && !next.startsWith('//')) {
        sessionStorage.setItem('walytic_post_auth_next', next);
        setPostAuthNext(next);
      } else if (invite) {
        const inviteNext = `/invite/${encodeURIComponent(invite)}`;
        sessionStorage.setItem('walytic_post_auth_next', inviteNext);
        setPostAuthNext(inviteNext);
      }
    } catch {
      // sessionStorage unavailable — silently ignore
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear field-level error on edit
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!formData.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Enter a valid email';
    if (!formData.phone.trim()) e.phone = 'WhatsApp number is required';
    else if (!/^\d{10,15}$/.test(formData.phone.replace(/\s|\+/g, ''))) e.phone = 'Use country code + number, digits only (e.g. 919876543210)';
    if (!formData.password) e.password = 'Password is required';
    else if (formData.password.length < 8) e.password = 'At least 8 characters';
    if (!formData.acceptTerms) e.acceptTerms = 'Please accept the terms';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev?.preventDefault?.();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    try {
      setLoading(true);
      await api.post('/api/auth/signup', {
        email: formData.email.trim(),
        password: formData.password,
        phone: formData.phone.trim(),
        recaptchaToken,
      });

      // GA4 funnel event — form submitted, OTP step starts
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'sign_up_started',
          method: 'email_otp',
        });
      }

      navigate('/verify-otp', { state: { email: formData.email.trim() } });
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || 'Signup failed. Please try again.';
      // Map common backend errors to field-level for clearer feedback
      if (/email/i.test(msg) && /exists|taken|already/i.test(msg)) {
        setErrors({ email: 'An account with this email already exists' });
      } else {
        modal.alert(msg, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page flex min-h-screen items-center justify-center p-4">
      <div className="auth-shell flex w-full max-w-[800px] flex-col overflow-hidden bg-surface-card md:flex-row">

        {/* LEFT: Signup Form */}
        <form
          onSubmit={handleSubmit}
          className="w-full md:flex-1 p-6 md:p-8 flex flex-col justify-center"
          noValidate
        >
          <div className="flex items-center mb-6 md:mb-8">
            <img
              src="/walytic-logo-blue.png"
              alt="Walytic"
              className="h-8 object-contain dark:hidden"
            />
            <img
              src="/walytic-logo-white.png"
              alt="Walytic"
              className="h-8 object-contain hidden dark:block"
            />
          </div>

          <h2 className="text-2xl font-bold text-ink-primary mb-1">Create Account</h2>
          <p className="text-ink-muted text-sm mb-6">Free to start. No credit card required.</p>

          <div className="input-group mb-4">
            <label className="input-label" htmlFor="signup-email">Email</label>
            <input
              id="signup-email"
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              className={`input ${errors.email ? 'border-red-500 focus:border-red-500' : ''}`}
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>

          <div className="input-group mb-4">
            <label className="input-label" htmlFor="signup-phone">WhatsApp Number</label>
            <input
              id="signup-phone"
              type="tel"
              name="phone"
              autoComplete="tel"
              inputMode="tel"
              placeholder="919876543210"
              className={`input ${errors.phone ? 'border-red-500 focus:border-red-500' : ''}`}
              value={formData.phone}
              onChange={handleChange}
            />
            {errors.phone ? (
              <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
            ) : (
              <p className="text-xs text-ink-muted mt-1">Country code + number, no + or spaces</p>
            )}
          </div>

          <div className="input-group mb-4">
            <label className="input-label" htmlFor="signup-password">Password</label>
            <div className="relative">
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                className={`input pr-10 ${errors.password ? 'border-red-500 focus:border-red-500' : ''}`}
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-primary"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>
            {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
          </div>

          <label className="flex items-start gap-2 mb-5 cursor-pointer text-sm text-ink-secondary">
            <input
              type="checkbox"
              name="acceptTerms"
              checked={formData.acceptTerms}
              onChange={handleChange}
              className="w-4 h-4 mt-0.5 rounded border-surface-border text-brand accent-brand"
            />
            <span>
              I accept the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">terms of service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">privacy policy</a>
            </span>
          </label>
          {errors.acceptTerms && <p className="text-xs text-red-500 -mt-3 mb-3">{errors.acceptTerms}</p>}

          <div className="mb-4">
            <Recaptcha onVerify={setRecaptchaToken} onExpire={() => setRecaptchaToken('')} theme="light" />
          </div>

          <button
            type="submit"
            className="btn-primary w-full mb-4"
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Sign Up'}
          </button>

          <p className="text-center text-sm text-ink-muted">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate(postAuthNext ? `/login?next=${encodeURIComponent(postAuthNext)}` : '/login')}
              className="text-brand font-semibold hover:underline"
            >
              Sign in
            </button>
          </p>
        </form>

        {/* RIGHT: Welcome Panel — hidden on mobile */}
        <div className="enterprise-auth-panel hidden flex-1 flex-col items-center justify-center p-8 text-white md:flex">
          <h3 className="font-bold text-2xl mb-4">Welcome to Walytic</h3>
          <p className="text-center text-white/90 mb-6 text-sm leading-relaxed">
            The WhatsApp automation platform built for teams that need bulk messaging,
            multi-device sessions, and built-in CRM.
          </p>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <FaCheckCircle size={14} className="text-white/80" />
              <span>Multi-device WhatsApp connections</span>
            </div>
            <div className="flex items-center gap-2">
              <FaCheckCircle size={14} className="text-white/80" />
              <span>Bulk messaging and templates</span>
            </div>
            <div className="flex items-center gap-2">
              <FaCheckCircle size={14} className="text-white/80" />
              <span>Team collaboration tools</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
